import React, { useEffect } from 'react';
import { Grid, Typography, Box, CircularProgress, useTheme, Card, CardContent } from '@mui/material';
import { NewReleases, History, VisibilityOff } from '@mui/icons-material';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';

const ProductFreshnessScore: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<{
        newProductsCount: number;
        newProductsViews: number;
        oldProductsCount: number;
        oldProductsViews: number;
        newProductsZeroViews: number;
    } | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await analyticsService.getProductFreshness(30);
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch product freshness');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch product freshness');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const newAvgViews = data && data.newProductsCount > 0 
        ? (data.newProductsViews / data.newProductsCount).toFixed(1) 
        : '0.0';
    const oldAvgViews = data && data.oldProductsCount > 0 
        ? (data.oldProductsViews / data.oldProductsCount).toFixed(1) 
        : '0.0';

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Product Freshness Score
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    New products (last 30 days) vs older products
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
                    <Typography variant='body2' color='error' fontFamily='primaryFont'>
                        {error}
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                    <NewReleases sx={{ fontSize: 20, color: theme.palette.success.main }} />
                                    <Typography variant='subtitle2' fontFamily='primaryFont' fontWeight={600}>
                                        New Products
                                    </Typography>
                                </Box>
                                <Typography variant='h4' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 1 }}>
                                    {data?.newProductsCount || 0}
                                </Typography>
                                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont' sx={{ mb: 0.5 }}>
                                    {data?.newProductsViews || 0} total views
                                </Typography>
                                <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                    {newAvgViews} avg views/product
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                    <History sx={{ fontSize: 20, color: theme.palette.info.main }} />
                                    <Typography variant='subtitle2' fontFamily='primaryFont' fontWeight={600}>
                                        Older Products
                                    </Typography>
                                </Box>
                                <Typography variant='h4' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 1 }}>
                                    {data?.oldProductsCount || 0}
                                </Typography>
                                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont' sx={{ mb: 0.5 }}>
                                    {data?.oldProductsViews || 0} total views
                                </Typography>
                                <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                    {oldAvgViews} avg views/product
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    {data && data.newProductsZeroViews > 0 && (
                        <Grid item xs={12}>
                            <Card sx={{ border: `1px solid ${theme.palette.warning.main}`, backgroundColor: `${theme.palette.warning.main}10` }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <VisibilityOff sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                                        <Box>
                                            <Typography variant='subtitle2' fontFamily='primaryFont' fontWeight={600} color='warning.main'>
                                                {data.newProductsZeroViews} new products have zero views
                                            </Typography>
                                            <Typography variant='caption' color='text.secondary' fontFamily='primaryFont'>
                                                Consider promoting these products
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            )}
        </PortalCard>
    );
};

export default ProductFreshnessScore;

